// Relative path: src/components/Recommendations/BannerHeader.js

import React from 'react';

// Import SVG icons
import estimatedCostIcon from '../../images/estimated_cost.svg';
import estimatedAvailabilityIcon from '../../images/estimated_availability.svg';

/**
 * Banner header component that shows a collapsed version when the main header scrolls out of view
 * 
 * @param {Object} props
 * @param {Object} props.option - Option data object
 * @param {boolean} props.isVisible - Whether the banner should be visible
 * @param {Function} props.onClickScroll - Function to call when banner is clicked to scroll to top
 */
const BannerHeader = ({ option, isVisible, onClickScroll }) => {
  
  // Extract data from option prop or use defaults
  const {
    imageUrl = '',
    title = 'Option Title',
    estimatedCost = '4000-7000',
    estimatedAvailability = 'Unknown',
    optionIndex = 0
  } = option || {};

  // Get the background color based on option index
  const getBackgroundColor = () => {
    const colors = ['bg-option1_main_soft', 'bg-option2_main_soft', 'bg-option3_main_soft'];
    return colors[optionIndex % colors.length] || 'bg-option1_main';
  };

  // Get the background opacity class
  const bgOpacityClass = 'bg-opacity-100';

  // Handle banner click
  const handleClick = () => {
    if (onClickScroll) {
      onClickScroll();
    }
  };

  return (
    <div 
      className={`
        absolute top-0 left-0 right-0 z-30 border-b border-gray-200 shadow-lg
        transition-all duration-300 ease-out bg-blue-50 cursor-pointer
        hover:bg-blue-100 active:bg-blue-200
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}
      `}
      onClick={handleClick}
    >
      <div className="flex items-center px-8 py-2">
        {/* Image/Avatar - Small size for banner */}
        <div className="w-14 h-14 bg-gray-200 rounded-lg mr-3 flex-shrink-0">
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt={title}
              className="w-full h-full object-cover rounded-lg"
            />
          )}
        </div>
        
        {/* Title and info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-800 text-sm truncate" title={title}>
            {title}
          </h3>
          
          {/* Compact info display */}
          <div className="flex items-center mt-1 space-x-4">
            <div className="flex items-center">
              <img 
                src={estimatedCostIcon}
                alt="Cost" 
                className="w-4 h-4 mr-1"
              />
              <span className="text-xs text-green-600 font-medium">${estimatedCost}</span>
            </div>
            
            <div className="flex items-center">
              <img 
                src={estimatedAvailabilityIcon}
                alt="Availability" 
                className="w-4 h-4 mr-1"
              />
              <span className="text-xs text-green-600 font-medium">{estimatedAvailability}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerHeader;